import type {
	SketchEntityReferenceSelectionMutationInput,
	SketchEntityReferenceSelectionMutationResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionIntentCollection';
import type { SketchEntityReferenceEffectiveSourcePlan } from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionSourceCompiler';
import type {
	SketchEntityReferenceSelectionItem,
	SketchEntityReferenceUIBridgeOptions,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceUIBridge';
import type { CreateGeometryReferencesInput } from '../../model/SketchEntityReferenceTypes';
import { SketchEntityReferenceSelectionIntentCollection } from './SketchEntityReferenceSelectionIntentCollection';
import { SketchEntityReferenceSelectionSourceCompiler } from './SketchEntityReferenceSelectionSourceCompiler';
import type {
	SketchEntityReferenceSelectionCandidate,
} from './SketchEntityReferenceSelectionAdapter';
import type {
	TopologyFaceReferenceSourceDescriptor,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';

export type {
	SketchEntityReferenceSelectionItem,
	SketchEntityReferenceUIBridgeOptions,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceUIBridge';

/**
 * 草图实体引用 UI 桥接器。
 *
 * 说明：
 * 1. PropertyManager 选择真相由有序选择意图集合持有；
 * 2. Preview/Create 输入由同一个有效来源编译器生成；
 * 3. web-client 只调用任务级接口，不读取内部 Map 或重复实现兼容矩阵。
 */
export class SketchEntityReferenceUIBridge {
	/** 当前目标草图 ID。 */
	private readonly targetSketchId: string;
	/** 当前目标草图名称。 */
	private readonly targetSketchName?: string;
	/** 当前命令独占的有序选择意图集合。 */
	private readonly selectionCollection: SketchEntityReferenceSelectionIntentCollection;
	/** 唯一有效来源编译器。 */
	private readonly sourceCompiler = new SketchEntityReferenceSelectionSourceCompiler();
	/** 当前选择版本对应的缓存来源计划。 */
	private effectiveSourcePlan: SketchEntityReferenceEffectiveSourcePlan | null = null;

	/**
	 * 创建 UI 桥接器。
	 *
	 * @param options 当前目标草图上下文。
	 */
	public constructor(options: SketchEntityReferenceUIBridgeOptions) {
		this.targetSketchId = options.targetSketchId;
		this.targetSketchName = options.targetSketchName;
		this.selectionCollection = new SketchEntityReferenceSelectionIntentCollection({
			targetSketchId: options.targetSketchId,
		});
	}

	/**
	 * 应用一次规范选择操作。
	 *
	 * @param input 选择操作和候选或稳定意图 ID。
	 * @returns 原子 mutation 差量。
	 */
	public applySelectionOperation(
		input: SketchEntityReferenceSelectionMutationInput,
	): SketchEntityReferenceSelectionMutationResult {
		const mutation = this.selectionCollection.apply(input);
		if (mutation.changed) {
			// 只有选择集合真实变化时才使编译缓存失效。
			this.effectiveSourcePlan = null;
		}
		return mutation;
	}

	/**
	 * 原子加入“选择所有内环”解析出的 loop 候选。
	 *
	 * @param candidates 每条 inner Wire 对应的一条候选。
	 * @returns 整批选择 mutation。
	 */
	public selectAllInnerLoops(
		candidates: readonly SketchEntityReferenceSelectionCandidate[],
	): SketchEntityReferenceSelectionMutationResult {
		const mutation = this.selectionCollection.applyBatch(candidates);
		if (mutation.changed) {
			this.effectiveSourcePlan = null;
		}
		return mutation;
	}

	/**
	 * 读取当前已选 topology face 上下文。
	 *
	 * @returns 去重后的面来源；loop 不会反向充当“选择所有内环”的面输入。
	 */
	public getTopologyFaceSources(): TopologyFaceReferenceSourceDescriptor[] {
		const sourcesByIntentId = new Map<string, TopologyFaceReferenceSourceDescriptor>();
		this.selectionCollection.getSelections().forEach((selection) => {
			if (
				selection.intent.intentKind === 'topology-face'
				&& selection.intent.topologyFaceSource
			) {
				sourcesByIntentId.set(
					selection.intent.intentId,
					structuredClone(selection.intent.topologyFaceSource),
				);
			}
		});
		return [...sourcesByIntentId.values()];
	}

	/**
	 * 判断当前选择是否具备“选择所有内环”能力。
	 *
	 * @returns 至少一个已选 topology face 的当前 Puppy CAD 内核 Shape 包含 inner Wire 时返回 `true`。
	 */
	public canSelectAllInnerLoops(): boolean {
		return this.selectionCollection.getSelections().some((selection) => (
			selection.intent.intentKind === 'topology-face'
			&& (selection.innerLoopCount ?? 0) > 0
		));
	}

	/**
	 * 读取 PropertyManager 当前选择快照。
	 *
	 * @returns 按用户选择顺序排列的显示项。
	 */
	public getSelections(): SketchEntityReferenceSelectionItem[] {
		return this.selectionCollection.getSelections().map((selection, index) => ({
			selectionId: selection.intent.intentId,
			descriptor: selection.sources[0]!.descriptor,
			displayLabel: selection.intent.intentKind === 'single-source'
				? `边线${index + 1}`
				: selection.intent.displayLabel,
			displayKind: selection.intent.displayKind,
		}));
	}

	/**
	 * 清空当前命令的全部选择意图。
	 *
	 * @returns 清空操作差量。
	 */
	public clearSelections(): SketchEntityReferenceSelectionMutationResult {
		const mutation = this.selectionCollection.clear();
		if (mutation.changed) {
			this.effectiveSourcePlan = null;
		}
		return mutation;
	}

	/**
	 * 基于当前唯一有效来源计划构建创建输入。
	 *
	 * @returns Preview 与 Confirm 共用的创建输入。
	 */
	public buildCreateInput(): CreateGeometryReferencesInput {
		const plan = this.getEffectiveSourcePlan();
		return {
			targetSketchId: this.targetSketchId,
			sources: [...plan.sources],
			sourceDisplayLabelsByKey: {
				...plan.sourceDisplayLabelsByKey,
			},
			selectionIntentsBySourceKey: {
				...plan.selectionIntentsBySourceKey,
			},
			...(Object.keys(plan.runtimeTopologySourcesBySourceKey).length > 0
				? {
					runtimeTopologySourcesBySourceKey: {
						...plan.runtimeTopologySourcesBySourceKey,
					},
				}
				: {}),
			options: {
				restoreBasicConstraints: true,
				includeOptionalEqualRelations: true,
			},
		};
	}

	/**
	 * 读取目标草图 ID。
	 *
	 * @returns 当前目标草图 ID。
	 */
	public getTargetSketchId(): string {
		return this.targetSketchId;
	}

	/**
	 * 读取目标草图名称。
	 *
	 * @returns 当前目标草图名称。
	 */
	public getTargetSketchName(): string | undefined {
		return this.targetSketchName;
	}

	/**
	 * 读取当前选择版本的有效来源计划。
	 *
	 * @returns 缓存或新编译的唯一来源计划。
	 */
	private getEffectiveSourcePlan(): SketchEntityReferenceEffectiveSourcePlan {
		this.effectiveSourcePlan ??= this.sourceCompiler.compile(
			this.selectionCollection.getSelections(),
		);
		return this.effectiveSourcePlan;
	}
}
